import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Trash2, RotateCcw, Search, Settings, AlertTriangle, Package, CheckCircle2,
  Loader2, ShieldAlert, XCircle, FileText
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const RecycleBinPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, by_type: [] });
  const [items, setItems] = useState([]);
  const [collectionFilter, setCollectionFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  // settings
  const [settings, setSettings] = useState({ auto_purge_days: 0 });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsDraft, setSettingsDraft] = useState(0);
  const [savingSettings, setSavingSettings] = useState(false);

  // restore / delete dialogs
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [detailsTarget, setDetailsTarget] = useState(null);
  const [bulkAction, setBulkAction] = useState(null); // 'restore' | 'delete' | 'empty'
  const [actionBusy, setActionBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [statsRes, itemsRes, settingsRes] = await Promise.all([
        api.get('/admin/recycle-bin/stats'),
        api.get('/admin/recycle-bin', { params: { collection: collectionFilter || undefined, search: search || undefined, limit: 500 } }),
        api.get('/admin/recycle-bin/settings'),
      ]);
      setStats(statsRes.data);
      setItems(itemsRes.data.items || []);
      setSettings(settingsRes.data);
      setSettingsDraft(settingsRes.data.auto_purge_days ?? 0);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Doar administratorii pot accesa Cosul de gunoi');
      } else {
        toast.error('Eroare la incarcarea Cosului de gunoi');
      }
    } finally {
      setLoading(false);
    }
  }, [collectionFilter, search]);

  useEffect(() => {
    if (isAdmin) fetchAll();
  }, [fetchAll, isAdmin]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    if (selectedIds.length === items.length) setSelectedIds([]);
    else setSelectedIds(items.map(i => i.id));
  };

  const doRestore = async (id) => {
    try {
      setActionBusy(true);
      await api.post(`/admin/recycle-bin/${id}/restore`);
      toast.success('Element restaurat');
      setRestoreTarget(null);
      setSelectedIds(p => p.filter(x => x !== id));
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Eroare la restaurare');
    } finally {
      setActionBusy(false);
    }
  };

  const doPermanentDelete = async (id) => {
    try {
      setActionBusy(true);
      await api.delete(`/admin/recycle-bin/${id}`);
      toast.success('Sters definitiv');
      setDeleteTarget(null);
      setSelectedIds(p => p.filter(x => x !== id));
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Eroare la stergere');
    } finally {
      setActionBusy(false);
    }
  };

  const doBulkRestore = async () => {
    try {
      setActionBusy(true);
      const res = await api.post('/admin/recycle-bin/bulk-restore', { ids: selectedIds });
      toast.success(`${res.data.restored} elemente restaurate`);
      if (res.data.errors?.length) {
        toast.warning(`${res.data.errors.length} nu au putut fi restaurate`);
      }
      setBulkAction(null);
      setSelectedIds([]);
      await fetchAll();
    } catch (e) {
      toast.error('Eroare la restaurare in masa');
    } finally {
      setActionBusy(false);
    }
  };

  const doBulkDelete = async () => {
    try {
      setActionBusy(true);
      const res = await api.post('/admin/recycle-bin/bulk-delete', { ids: selectedIds });
      toast.success(`${res.data.deleted} elemente sterse definitiv`);
      setBulkAction(null);
      setSelectedIds([]);
      await fetchAll();
    } catch (e) {
      toast.error('Eroare la stergere in masa');
    } finally {
      setActionBusy(false);
    }
  };

  const doEmptyBin = async () => {
    try {
      setActionBusy(true);
      const params = collectionFilter ? { collection: collectionFilter } : {};
      const res = await api.post('/admin/recycle-bin/empty', null, { params });
      toast.success(`Cosul a fost golit: ${res.data.deleted} elemente sterse definitiv`);
      setBulkAction(null);
      setSelectedIds([]);
      await fetchAll();
    } catch (e) {
      toast.error('Eroare la golire');
    } finally {
      setActionBusy(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSavingSettings(true);
      await api.put('/admin/recycle-bin/settings', { auto_purge_days: parseInt(settingsDraft || 0, 10) });
      toast.success('Setari salvate');
      setSettingsOpen(false);
      await fetchAll();
    } catch (e) {
      toast.error('Eroare la salvare setari');
    } finally {
      setSavingSettings(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleString('ro-RO', { dateStyle: 'short', timeStyle: 'short' });
    } catch { return iso; }
  };

  const retentionLabel = useMemo(() => {
    const d = settings?.auto_purge_days || 0;
    if (!d) return 'Nelimitat';
    return `${d} zile`;
  }, [settings]);

  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card className="border-rose-200 bg-rose-50">
          <CardContent className="p-6 flex items-center gap-3">
            <ShieldAlert className="w-6 h-6 text-rose-600" />
            <div>
              <p className="font-semibold text-rose-900">Acces restrictionat</p>
              <p className="text-sm text-rose-700">Doar administratorii pot accesa Cosul de gunoi.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="recycle-bin-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Trash2 className="w-7 h-7 text-amber-600" />
            Cos de gunoi
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Documente sterse recuperabile. Retentie configurabila:
            <span className="ml-1 font-semibold text-slate-700">{retentionLabel}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setSettingsOpen(true)} data-testid="open-settings-btn">
            <Settings className="w-4 h-4 mr-2" /> Setari retentie
          </Button>
          <Button
            variant="destructive"
            disabled={stats.total === 0}
            onClick={() => setBulkAction('empty')}
            data-testid="empty-bin-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Goleste {collectionFilter ? 'categoria' : 'tot cosul'}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Package className="w-4 h-4" /> Stare Cos ({stats.total} elemente)
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Badge
            variant={!collectionFilter ? 'default' : 'outline'}
            className="cursor-pointer px-3 py-1.5"
            onClick={() => { setCollectionFilter(''); setSelectedIds([]); }}
            data-testid="filter-all-btn"
          >
            Toate ({stats.total})
          </Badge>
          {stats.by_type.map(t => (
            <Badge
              key={t.collection}
              variant={collectionFilter === t.collection ? 'default' : 'outline'}
              className="cursor-pointer px-3 py-1.5"
              onClick={() => { setCollectionFilter(t.collection); setSelectedIds([]); }}
              data-testid={`filter-${t.collection}-btn`}
            >
              {t.label}: {t.count}
            </Badge>
          ))}
        </CardContent>
      </Card>

      {/* Search + bulk actions */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Cauta dupa nume, tip, user sau motiv..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') fetchAll(); }}
            className="pl-9"
            data-testid="search-input"
          />
        </div>
        <Button variant="outline" onClick={fetchAll} data-testid="refresh-btn">
          <Loader2 className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : 'hidden'}`} />
          Cauta
        </Button>
        {selectedIds.length > 0 && (
          <div className="flex gap-2 ml-auto">
            <Badge variant="secondary" className="px-3 py-1">{selectedIds.length} selectate</Badge>
            <Button variant="outline" size="sm" onClick={() => setBulkAction('restore')} data-testid="bulk-restore-btn">
              <RotateCcw className="w-4 h-4 mr-2" /> Restaureaza
            </Button>
            <Button variant="destructive" size="sm" onClick={() => setBulkAction('delete')} data-testid="bulk-delete-btn">
              <Trash2 className="w-4 h-4 mr-2" /> Sterge definitiv
            </Button>
          </div>
        )}
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
              Se incarca...
            </div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center">
              <Trash2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">Cosul este gol</p>
              <p className="text-xs text-slate-400 mt-1">Nicio stergere recenta.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="p-3 text-left w-10">
                      <input
                        type="checkbox"
                        checked={selectedIds.length === items.length && items.length > 0}
                        onChange={toggleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th className="p-3 text-left">Tip</th>
                    <th className="p-3 text-left">Nume / Identificator</th>
                    <th className="p-3 text-left">Sters de</th>
                    <th className="p-3 text-left">Data stergere</th>
                    <th className="p-3 text-left">Motiv</th>
                    <th className="p-3 text-right">Actiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id} className="border-t hover:bg-slate-50">
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(it.id)}
                          onChange={() => toggleSelect(it.id)}
                          data-testid={`row-checkbox-${it.id}`}
                        />
                      </td>
                      <td className="p-3">
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                          {it.entity_type}
                        </Badge>
                      </td>
                      <td className="p-3 font-medium text-slate-800">
                        <button
                          className="hover:underline text-left"
                          onClick={() => setDetailsTarget(it)}
                          data-testid={`details-btn-${it.id}`}
                        >
                          {it.display_name || '(fara nume)'}
                        </button>
                      </td>
                      <td className="p-3 text-slate-600">{it.deleted_by_name || '-'}</td>
                      <td className="p-3 text-slate-600">{formatDate(it.deleted_at)}</td>
                      <td className="p-3 text-slate-500 italic max-w-[200px] truncate">{it.delete_reason || '-'}</td>
                      <td className="p-3 text-right">
                        <div className="flex gap-2 justify-end">
                          <Button size="sm" variant="outline" onClick={() => setRestoreTarget(it)} data-testid={`restore-btn-${it.id}`}>
                            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restaureaza
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => setDeleteTarget(it)} data-testid={`delete-btn-${it.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent data-testid="settings-dialog">
          <DialogHeader>
            <DialogTitle>Setari retentie Cos de gunoi</DialogTitle>
            <DialogDescription>
              Configureaza dupa cate zile se sterg automat (definitiv) elementele din Cos.
              Setat la 0 = nu se sterg niciodata automat.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="days">Zile retentie</Label>
              <Select value={String(settingsDraft)} onValueChange={(v) => setSettingsDraft(parseInt(v, 10))}>
                <SelectTrigger id="days" data-testid="settings-days-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Niciodata (nelimitat)</SelectItem>
                  <SelectItem value="30">30 zile</SelectItem>
                  <SelectItem value="60">60 zile</SelectItem>
                  <SelectItem value="90">90 zile</SelectItem>
                  <SelectItem value="180">180 zile</SelectItem>
                  <SelectItem value="365">1 an</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-2">
                Verificarea ruleaza automat la pornirea serverului (zilnic).
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>Anuleaza</Button>
            <Button onClick={saveSettings} disabled={savingSettings} data-testid="save-settings-btn">
              {savingSettings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Salveaza
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore Confirm */}
      <Dialog open={!!restoreTarget} onOpenChange={(o) => !o && setRestoreTarget(null)}>
        <DialogContent data-testid="restore-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-blue-600" /> Restaureaza element
            </DialogTitle>
            <DialogDescription>
              Vei restaura <strong>{restoreTarget?.entity_type}</strong> &quot;{restoreTarget?.display_name}&quot;
              inapoi in sistem. Aceasta actiune il va face din nou vizibil si utilizabil.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreTarget(null)}>Anuleaza</Button>
            <Button onClick={() => doRestore(restoreTarget.id)} disabled={actionBusy} data-testid="confirm-restore-btn">
              {actionBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RotateCcw className="w-4 h-4 mr-2" />}
              Restaureaza
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permanent Delete Confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600">
              <AlertTriangle className="w-5 h-5" /> Stergere definitiva
            </DialogTitle>
            <DialogDescription>
              Vei sterge <strong>DEFINITIV</strong> {deleteTarget?.entity_type} &quot;{deleteTarget?.display_name}&quot;.
              <br />
              <span className="text-rose-700 font-semibold">Aceasta actiune este IREVERSIBILA.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Anuleaza</Button>
            <Button variant="destructive" onClick={() => doPermanentDelete(deleteTarget.id)} disabled={actionBusy} data-testid="confirm-delete-btn">
              {actionBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Sterge definitiv
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk actions confirm */}
      <Dialog open={!!bulkAction} onOpenChange={(o) => !o && setBulkAction(null)}>
        <DialogContent data-testid="bulk-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {bulkAction === 'restore' && <RotateCcw className="w-5 h-5 text-blue-600" />}
              {bulkAction === 'delete' && <AlertTriangle className="w-5 h-5 text-rose-600" />}
              {bulkAction === 'empty' && <Trash2 className="w-5 h-5 text-rose-600" />}
              {bulkAction === 'restore' && 'Restaurare in masa'}
              {bulkAction === 'delete' && 'Stergere definitiva in masa'}
              {bulkAction === 'empty' && (collectionFilter ? 'Goleste categoria' : 'Goleste tot cosul')}
            </DialogTitle>
            <DialogDescription>
              {bulkAction === 'restore' && `Restaurezi ${selectedIds.length} elemente inapoi in sistem.`}
              {bulkAction === 'delete' && (
                <>
                  Vei sterge <strong>DEFINITIV</strong> {selectedIds.length} elemente.{' '}
                  <span className="text-rose-700 font-semibold">IREVERSIBIL.</span>
                </>
              )}
              {bulkAction === 'empty' && (
                <>
                  Vei sterge <strong>DEFINITIV</strong>{' '}
                  {collectionFilter
                    ? `toate elementele din categoria "${stats.by_type.find(t => t.collection === collectionFilter)?.label || collectionFilter}"`
                    : `toate cele ${stats.total} elemente`}
                  . <span className="text-rose-700 font-semibold">IREVERSIBIL.</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkAction(null)}>Anuleaza</Button>
            {bulkAction === 'restore' && (
              <Button onClick={doBulkRestore} disabled={actionBusy} data-testid="confirm-bulk-restore-btn">
                {actionBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                Restaureaza {selectedIds.length}
              </Button>
            )}
            {bulkAction === 'delete' && (
              <Button variant="destructive" onClick={doBulkDelete} disabled={actionBusy} data-testid="confirm-bulk-delete-btn">
                {actionBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                Sterge definitiv {selectedIds.length}
              </Button>
            )}
            {bulkAction === 'empty' && (
              <Button variant="destructive" onClick={doEmptyBin} disabled={actionBusy} data-testid="confirm-empty-btn">
                {actionBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Goleste
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={!!detailsTarget} onOpenChange={(o) => !o && setDetailsTarget(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="details-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" /> Detalii document sters
            </DialogTitle>
            <DialogDescription>
              {detailsTarget?.entity_type} - {detailsTarget?.display_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Sters de</Label>
                <p className="font-medium">{detailsTarget?.deleted_by_name || '-'}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Data stergerii</Label>
                <p className="font-medium">{formatDate(detailsTarget?.deleted_at)}</p>
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-slate-500">Motiv</Label>
                <p className="italic">{detailsTarget?.delete_reason || '-'}</p>
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-slate-500">Date originale (JSON)</Label>
                <pre className="text-xs bg-slate-50 border rounded p-2 overflow-x-auto whitespace-pre-wrap max-h-60">
{JSON.stringify(detailsTarget?.original_data || {}, null, 2)}
                </pre>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsTarget(null)}>Inchide</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecycleBinPage;
